import {
  PUBLIC_MASK_PERSONAS,
  PUBLIC_MASK_RESOURCE_TEMPLATES,
  PUBLIC_MASK_EMAIL_DOMAINS,
} from '../data/publicMaskAliases'
import type {
  MaskResolverContext,
  PublicMaskPersona,
  PublicWorkspaceMaskingConfig,
} from '../types/publicWorkspaceMask'

const VIEWER_SESSION_STORAGE_KEY = 'timelining:public-mask-viewer-session'

/**
 * 32-bit FNV-1a deterministic hash. Works identically in Node.js and browser environments.
 */
export function hashString(input: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

/**
 * Get or create a persistent viewer session ID for the current browser session.
 */
export function getOrCreateViewerSessionId(): string {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return 'default-session'
  }
  try {
    let sessionId = window.sessionStorage.getItem(VIEWER_SESSION_STORAGE_KEY)
    if (!sessionId) {
      sessionId = 'sess_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now().toString(36)
      window.sessionStorage.setItem(VIEWER_SESSION_STORAGE_KEY, sessionId)
    }
    return sessionId
  } catch {
    return 'fallback-session'
  }
}

/**
 * Deterministic Alias Resolver for Public Workspace Masking (D38, D39).
 */
export class PublicWorkspaceMaskResolver {
  private context: MaskResolverContext

  constructor(context: MaskResolverContext) {
    this.context = {
      workspaceId: context.workspaceId,
      viewerSessionId: context.viewerSessionId || 'default-session',
      seed: context.seed || 'default-seed',
      customAliases: context.customAliases,
    }
  }

  private compositeKey(subsystem: string, identifier: string): string {
    return `${this.context.viewerSessionId}:${this.context.workspaceId}:${this.context.seed}:${subsystem}:${identifier}`
  }

  /**
   * Resolves a deterministic persona alias for a workspace member.
   */
  resolveMemberAlias(uid: string): PublicMaskPersona {
    if (this.context.customAliases?.members && this.context.customAliases.members[uid]) {
      return this.context.customAliases.members[uid]
    }
    const hash = hashString(this.compositeKey('member', uid))
    const index = hash % PUBLIC_MASK_PERSONAS.length
    return PUBLIC_MASK_PERSONAS[index]
  }

  /**
   * Resolves a masked external account identity (e.g. connected Google/Notion email).
   */
  resolveExternalAccountAlias(accountId: string, realEmailOrName?: string): string {
    if (this.context.customAliases?.externalAccounts && this.context.customAliases.externalAccounts[accountId]) {
      return this.context.customAliases.externalAccounts[accountId]
    }
    const hash = hashString(this.compositeKey('ext-account', accountId))
    const persona = PUBLIC_MASK_PERSONAS[hash % PUBLIC_MASK_PERSONAS.length]
    const domain = PUBLIC_MASK_EMAIL_DOMAINS[hash % PUBLIC_MASK_EMAIL_DOMAINS.length]
    const username = persona.name.toLowerCase().replace(/\s+/g, '.')
    if (realEmailOrName && realEmailOrName.includes('@')) {
      return `${username}@${domain}`
    }
    return `${persona.name} (${domain})`
  }

  /**
   * Resolves a masked external resource title (e.g. Notion page, Jira project, Drive folder).
   */
  resolveExternalResourceAlias(resourceId: string): string {
    if (this.context.customAliases?.externalResources && this.context.customAliases.externalResources[resourceId]) {
      return this.context.customAliases.externalResources[resourceId]
    }
    const hash = hashString(this.compositeKey('ext-resource', resourceId))
    const index = hash % PUBLIC_MASK_RESOURCE_TEMPLATES.length
    return PUBLIC_MASK_RESOURCE_TEMPLATES[index]
  }

  /**
   * Masks an attachment thumbnail by returning a generated neutral SVG placeholder or null.
   */
  maskThumbnail(thumbnailUrl?: string | null): string | null {
    if (!thumbnailUrl) return null
    // Neutral placeholder SVG with doc icon
    return 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="%2364748b" viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>'
  }

  /**
   * Transforms a member profile record by masking PII fields.
   */
  maskMemberRecord<T extends Record<string, unknown>>(member: T): T {
    const uid = String(member.userId || member.uid || member.id || '')
    const alias = this.resolveMemberAlias(uid)
    return {
      ...member,
      displayName: alias.name,
      name: alias.name,
      email: alias.email,
      initials: alias.initials,
      photoURL: null,
      avatarUrl: null,
      colorSeed: alias.colorSeed,
      isMasked: true,
    }
  }

  /**
   * Transforms a storage connection record by masking external account identity.
   */
  maskStorageConnection<T extends Record<string, unknown>>(connection: T): T {
    const accountId = String(connection.externalAccountId || connection.id || '')
    const maskedIdentity = this.resolveExternalAccountAlias(accountId, String(connection.displayName || ''))
    const providerRaw = String(connection.provider || '')
    const providerTitle = providerRaw
      ? providerRaw.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')
      : 'External Connection'
    return {
      ...connection,
      displayName: `${providerTitle} (Demo Account)`,
      externalAccountId: maskedIdentity,
      creatorName: 'Demo Team Member',
      isMasked: true,
    }
  }

  /**
   * Transforms an element or checklist item by masking providerMappings and attachment thumbnails,
   * while keeping freeform user titles, notes, and timestamps untouched (D39).
   */
  maskElementRecord<T extends Record<string, unknown>>(element: T): T {
    const modified: Record<string, unknown> = { ...element }

    // Mask providerMappings if present
    if (element.providerMappings && typeof element.providerMappings === 'object') {
      const originalMappings = element.providerMappings as Record<string, Record<string, unknown>>
      const maskedMappings: Record<string, Record<string, unknown>> = {}
      for (const [providerKey, mapping] of Object.entries(originalMappings)) {
        if (mapping && typeof mapping === 'object') {
          const resourceId = String(mapping.externalId || mapping.id || providerKey)
          maskedMappings[providerKey] = {
            ...mapping,
            externalTitle: this.resolveExternalResourceAlias(resourceId),
            externalResourceName: this.resolveExternalResourceAlias(resourceId),
            externalUrl: 'https://example.com/demo-resource',
          }
        } else {
          maskedMappings[providerKey] = mapping
        }
      }
      modified.providerMappings = maskedMappings
    }

    // Mask attachment thumbnails if present
    if (element.thumbnailUrl) {
      modified.thumbnailUrl = this.maskThumbnail(String(element.thumbnailUrl))
    }
    if (Array.isArray(element.attachments)) {
      modified.attachments = (element.attachments as Array<Record<string, unknown>>).map((att) => ({
        ...att,
        thumbnailUrl: att.thumbnailUrl ? this.maskThumbnail(String(att.thumbnailUrl)) : null,
      }))
    }

    return modified as T
  }
}

/**
 * Convenience helper to create a mask resolver.
 */
export function createPublicWorkspaceMaskResolver(
  workspaceId: string,
  config?: PublicWorkspaceMaskingConfig | null,
  viewerSessionId?: string,
): PublicWorkspaceMaskResolver {
  return new PublicWorkspaceMaskResolver({
    workspaceId,
    viewerSessionId: viewerSessionId || getOrCreateViewerSessionId(),
    seed: config?.seed,
    customAliases: config?.customAliases,
  })
}

export const createMaskResolver = createPublicWorkspaceMaskResolver

